import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import DOMPurify from 'dompurify';
import InputModal from '../InputModal';
import { COLORS } from '../../../styles/constants';

const SchematicSVG = ({ schematicPath, simulationNumber }) => {
	const partDefinition = useSelector(state => state.designDefinition.partDefinition);
	const simulationParameters = partDefinition.simulations[simulationNumber].inputParameters;
	const inputValues = useSelector(state => state.modelData.inputParams[simulationNumber]);
	const [processedSchematic, setProcessedSchematic] = useState(null);
	const [isModalOpen, setModalOpen] = useState(false);
	const [currentInput, setCurrentInput] = useState({ value: '', formattedName: '', unit: '', mappingName: '' });
	const containerRef = useRef();

	useEffect(() => {
		if (schematicPath) {
			fetch(schematicPath)
				.then(response => response.text())
				.then(data => {
					const sanitizedSVG = DOMPurify.sanitize(data);
					const processedSchematic = processSchematicComponents(sanitizedSVG);
					setProcessedSchematic(processedSchematic);
				})
				.catch(error => console.error('Error fetching schematic SVG:', error));
		}
		//eslint-disable-next-line
	}, [schematicPath, inputValues]);

	useEffect(() => {
		if (processedSchematic && containerRef.current) {
			const shadowRoot = containerRef.current.shadowRoot || containerRef.current.attachShadow({ mode: 'open' });
			shadowRoot.innerHTML = `<style>svg { width: 100%; height: auto; }</style>${processedSchematic}`;
			attachEventListeners(shadowRoot);
		}
		//eslint-disable-next-line
	}, [processedSchematic]);

	const attachEventListeners = shadowRoot => {
		shadowRoot.querySelectorAll('[data-value]').forEach(element => {
			element.setAttribute('cursor', 'pointer');
			element.addEventListener('click', () => {
				const value = element.getAttribute('data-value');
				const formattedName = element.getAttribute('data-formatted-string');
				const unit = element.getAttribute('data-unit');
				const mappingName = element.getAttribute('data-mapping-name');
				handleValueClick(value, formattedName, unit, mappingName);
			});
		});
	};

	const getInputValue = mappingName => {
		const input = Object.values(inputValues).find(input => input.mappingName === mappingName);
		return input ? input.value : null;
	};

	const handleValueClick = (value, formattedName, unit, mappingName) => {
		setCurrentInput({ value, formattedName, unit, mappingName });
		document.activeElement.blur();
		setModalOpen(true);
	};

	const handleModalClose = () => {
		setModalOpen(false);
	};

	const setAttributes = (element, attributes) => {
		for (const key in attributes) {
			element.setAttribute(key, attributes[key]);
		}
	};

	const processSchematicComponents = code => {
		const parser = new DOMParser();
		const svgDoc = parser.parseFromString(code, 'image/svg+xml');
		svgDoc.documentElement.setAttribute('id', 'schematic_el');

		simulationParameters.forEach(param => {
			const inputParam = svgDoc.getElementById(param.mappingName);
			const inputValue = getInputValue(param.mappingName);

			if (inputParam && inputValue !== null) {
				const formattedName = formatSVGString(param.heading);

				const labelTspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
				const equalSignTspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
				const valueTspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
				const unitTspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');

				valueTspan.setAttribute('fill', COLORS.PRIMARY);
				valueTspan.setAttribute('font-size', '1em');
				labelTspan.setAttribute('font-size', '1em');
				unitTspan.setAttribute('font-size', '1em');

				labelTspan.innerHTML = formattedName;
				equalSignTspan.innerHTML = ' = ';
				equalSignTspan.setAttribute('dy', '-0.5em');
				valueTspan.innerHTML = inputValue;
				unitTspan.innerHTML = ` ${param.unit}`;

				const attributes = {
					'data-value': inputValue,
					'data-formatted-string': formattedName,
					'data-mapping-name': param.mappingName,
					'data-unit': param.unit
				};

				setAttributes(valueTspan, attributes);
				setAttributes(labelTspan, attributes);
				setAttributes(equalSignTspan, attributes);
				setAttributes(unitTspan, attributes);

				inputParam.innerHTML = '';
				inputParam.appendChild(labelTspan);
				inputParam.appendChild(equalSignTspan);
				inputParam.appendChild(valueTspan);
				inputParam.appendChild(unitTspan);
			}
		});

		const schematicOPN = svgDoc.getElementById('schematic-OPN');
		if (schematicOPN) {
			schematicOPN.innerHTML = partDefinition.mainHeading;
		}

		return svgDoc.documentElement.outerHTML;
	};

	const formatSVGString = str => {
		return str.replace(/<sub>/g, '<tspan dy="0.5em" font-size="0.7em">').replace(/<\/sub>/g, '</tspan>');
	};

	if (!processedSchematic) {
		return <span />;
	}

	return (
		<>
			<div
				className={'schematic_container' + simulationNumber}
				ref={containerRef}
				style={{ width: '100%', height: '100%' }}></div>
			<InputModal
				isOpen={isModalOpen}
				onClose={handleModalClose}
				value={currentInput.value}
				formattedName={currentInput.formattedName}
				unit={currentInput.unit}
				mappingName={currentInput.mappingName}
				simulationNumber={simulationNumber}
			/>
		</>
	);
};

export default SchematicSVG;
